/**
 * useLiveState — small environment hooks for the live operator surfaces
 * (DIGEMP-FINISH-PRODUCT-EXPERIENCE-V2-140).
 *
 * Both read real browser signals only (matchMedia media-query result, navigator.onLine plus the
 * online/offline events) — no backend contract, no invented state. Environments without
 * matchMedia (jsdom, very old browsers) safely read as "motion allowed".
 */
import { useEffect, useRef, useState } from "react";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

export function prefersReducedMotionNow() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia(REDUCED_MOTION_QUERY).matches === true;
  } catch {
    return false;
  }
}

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(prefersReducedMotionNow);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    let mql;
    try {
      mql = window.matchMedia(REDUCED_MOTION_QUERY);
    } catch {
      return undefined;
    }
    setReduced(mql.matches === true);
    const onChange = (event) => setReduced(event.matches === true);
    if (typeof mql.addEventListener !== "function") return undefined;
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

export function useOnlineStatus() {
  const [online, setOnline] = useState(
    () => typeof navigator === "undefined" || navigator.onLine !== false
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    setOnline(navigator.onLine !== false);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  return online;
}

/**
 * Refetch read-only sources after a real offline→online transition.
 *
 * The browser's online signal is the only trigger. There is no first-mount call and no call when
 * the page was already online, so a reconnect cannot replay anything that happened while offline.
 * Each distinct refetch function runs at most once per transition; callers pass query refetchers
 * only — this hook never invokes a mutation or an action.
 *
 * `enabled` mirrors the page's read permission: a page that is not allowed to read does not fetch
 * on reconnect. If reads become enabled after the reconnect, the pending transition is consumed
 * then (still exactly once).
 */
export function useReconnectRefetch(refetchers, { enabled = true } = {}) {
  const online = useOnlineStatus();
  const wasOfflineRef = useRef(false);
  const refetchersRef = useRef(refetchers);
  refetchersRef.current = refetchers;

  useEffect(() => {
    if (!online) {
      wasOfflineRef.current = true;
      return;
    }
    if (!wasOfflineRef.current || !enabled) return;
    wasOfflineRef.current = false;

    const seen = new Set();
    const list = Array.isArray(refetchersRef.current) ? refetchersRef.current : [];
    list.forEach((refetch) => {
      if (typeof refetch !== "function" || seen.has(refetch)) return;
      seen.add(refetch);
      refetch();
    });
  }, [online, enabled]);

  return online;
}

/**
 * "Recovered" is a transition, not a paint: it is announced only after an unavailability was
 * actually observed on this screen (a degraded/failed source, or going offline) AND a successful
 * load happened after it (loadedAt newer than the failure) — so a mere reconnect without a
 * refresh never claims one. First paint, loading and failures never produce it; a new failure
 * clears it.
 *
 * `degraded` covers the caller's own failed/stale definition for this surface.
 */
export function useRecoveredNotice({ degraded = false, fresh = false, online = true, loadedAt = 0 } = {}) {
  const [recoveredAt, setRecoveredAt] = useState(null);
  const failureAtRef = useRef(null);

  useEffect(() => {
    if (!online || degraded) {
      failureAtRef.current = Date.now();
      setRecoveredAt(null);
      return;
    }
    if (fresh && failureAtRef.current != null && loadedAt > failureAtRef.current) {
      failureAtRef.current = null;
      setRecoveredAt(loadedAt);
    }
  }, [degraded, fresh, online, loadedAt]);

  return recoveredAt;
}
